/**
 * @file src/services/ReactQueryClientService.ts
 * @description This file sets up and exports a configured QueryClient instance for use with React Query.
 * It defines default options for queries, such as how long data should remain fresh (staleTime), retry logic,
 * and refetch behavior. Additionally, it configures query persistence using local storage, allowing query data
 * to persist across sessions and browser reloads, improving the user experience.
 *
 * Query persistence is only set up in the browser environment (to avoid server-side errors in environments like Next.js).
 *
 * @module ReactQueryClientService
 */

import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

/**
 * The `QueryClient` instance provides configuration for React Query, including settings for query caching,
 * retries on failure, and behavior on browser focus.
 *
 * @type {QueryClient}
 *
 * Default configuration:
 * - `staleTime`: Determines how long query data is considered fresh before it's marked as stale and eligible for refetching.
 *   - In this case, queries remain fresh for 5 minutes (300,000 milliseconds).
 *
 * - `retry`: Number of attempts to retry a failed query.
 *   - Here, failed queries will be retried up to 2 times before an error is thrown.
 *
 * - `refetchOnWindowFocus`: Whether queries should be refetched when the window is focused.
 *   - In this configuration, queries will **not** refetch when the user focuses back on the window, preventing unnecessary requests.
 *
 * Mutations:
 * - `retry`: Specifies the number of times a mutation should retry upon failure.
 *   - Mutations will retry once if they fail.
 */
const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Queries remain fresh for 5 minutes
      retry: 2, // Retry failed queries up to 2 times
      refetchOnWindowFocus: false, // Do not refetch when the window is focused
    },
    mutations: {
      retry: 1, // Retry mutations once upon failure
    },
  },
});

/**
 * Persister for React Query's cache using the browser's local storage.
 * This persists query results in localStorage so that the user doesn't lose
 * query data when they refresh or leave the page.
 */
let persister: ReturnType<typeof createSyncStoragePersister> | null = null;

/**
 * The following code sets up persistence for React Query's cache using the browser's local storage.
 *
 * Persistence means that query results are saved to local storage, allowing the user to refresh or leave the page
 * without losing previously fetched data. The cached data remains available for a specified period.
 *
 * - `maxAge`: Specifies how long the persisted data remains valid in milliseconds (12 hours here).
 *
 * To avoid errors during server-side rendering (SSR), the `window` object is checked to ensure that the persistence setup only runs in the browser.
 */
if (typeof window !== 'undefined') {
  // Create the persister only when `window` is available
  persister = createSyncStoragePersister({
    storage: window.localStorage, // Use local storage for persistence
  });

  persistQueryClient({
    queryClient: reactQueryClient,
    persister,
    maxAge: 12 * 60 * 60 * 1000, // Data is persisted for 12 hours
  });
}

export { persister };
export default reactQueryClient;
